<template>
  <div
    v-if="open"
    class="modal fade show d-block size-modal"
    tabindex="-1"
    aria-modal="true"
    role="dialog"
    @click.self="$emit('close')"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content border-0 shadow-lg rounded-4">
        <div class="modal-header border-bottom-dashed">
          <div>
            <span class="modal-chip">{{ isEdit ? $t("size_page.edit_chip") : $t("size_page.new_chip") }}</span>
            <h5 class="modal-title fw-bold mt-3 mb-1">
              {{ isEdit ? $t("size_page.update_title") : $t("size_page.create_title") }}
            </h5>
            <p class="text-muted mb-0">
              {{ $t("size_page.modal_description") }}
            </p>
          </div>
          <button type="button" class="btn-close shadow-none" :aria-label="$t('common.close')" @click="$emit('close')"></button>
        </div>

        <div class="modal-body p-4 p-xl-40">
          <div class="size-tip-card mb-4">
            <div class="tip-icon">
              <i class="ri-ruler-line"></i>
            </div>
            <div>
              <h6 class="mb-1">
                {{ isEdit ? $t("size_page.edit_tip_title") : $t("size_page.create_tip_title") }}
              </h6>
              <p class="mb-0 text-muted">
                {{ $t("size_page.tip_description") }}
              </p>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("size_page.code") }}</label>
                <span class="field-caption">{{ $t("size_page.code_caption") }}</span>
              </div>
              <input
                :value="form.code"
                type="text"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.code }"
                :placeholder="$t('size_page.code_placeholder')"
                @input="$emit('update-field', 'code', ($event.target as HTMLInputElement).value)"
              />
              <div v-if="errors.code" class="invalid-feedback d-block">{{ errors.code[0] }}</div>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("size_page.name") }}</label>
                <span class="field-caption">{{ $t("size_page.name_caption") }}</span>
              </div>
              <input
                :value="form.name"
                type="text"
                class="form-control modal-input"
                :class="{ 'is-invalid': errors.name }"
                :placeholder="$t('size_page.name_placeholder')"
                @input="$emit('update-field', 'name', ($event.target as HTMLInputElement).value)"
              />
              <div v-if="errors.name" class="invalid-feedback d-block">{{ errors.name[0] }}</div>
            </div>

            <div class="col-md-6">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("size_page.status") }}</label>
                <span class="field-caption">{{ $t("size_page.status_caption") }}</span>
              </div>
              <select
                :value="normalizeStatus(form.status)"
                class="form-select modal-input"
                @change="$emit('update-field', 'status', Number(($event.target as HTMLSelectElement).value))"
              >
                <option :value="1">{{ $t("size_page.status_active") }}</option>
                <option :value="0">{{ $t("size_page.status_inactive") }}</option>
              </select>
            </div>

            <div class="col-12">
              <div class="field-header">
                <label class="form-label fw-semibold text-title mb-0">{{ $t("size_page.description") }}</label>
                <span class="field-caption">{{ $t("size_page.description_caption") }}</span>
              </div>
              <textarea
                :value="form.description"
                rows="5"
                class="form-control modal-input modal-textarea"
                :placeholder="$t('size_page.description_placeholder')"
                @input="$emit('update-field', 'description', ($event.target as HTMLTextAreaElement).value)"
              ></textarea>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-4 pt-2">
            <button type="button" class="btn btn-light px-4" @click="$emit('close')">
              {{ $t("common.cancel") }}
            </button>
            <button type="button" class="btn style-one px-4 h-55" :disabled="loading" @click="$emit('save')">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ loading ? $t("size_page.saving") : isEdit ? $t("size_page.update_action") : $t("size_page.create_action") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import type { Size } from "@/services/size";

export default defineComponent({
  name: "CreateSizeModal",
  emits: ["close", "save", "update-field"],
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object as PropType<Size>,
      required: true,
    },
    errors: {
      type: Object as PropType<Record<string, string[]>>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const normalizeStatus = (value?: boolean | number) =>
      String(value ?? 1) === "0" || String(value ?? 1) === "false" ? 0 : 1;

    return {
      normalizeStatus,
    };
  },
});
</script>

<style scoped>
.size-modal {
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
}

.modal-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: #edf2ff;
  color: #5d5fef;
  font-weight: 700;
}

.size-tip-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px 18px;
  border: 1px solid #e6ebfb;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8faff 0%, #ffffff 100%);
}

.tip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: #eef2ff;
  color: #5d5fef;
  font-size: 20px;
}

.field-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.field-caption {
  color: #6b7280;
  font-size: 12px;
}

.modal-input {
  min-height: 54px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  background: #fff;
  color: #0f172a;
  box-shadow: none;
  padding: 14px 18px;
}

.modal-input:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.modal-textarea {
  min-height: 140px;
  resize: vertical;
  padding-top: 16px;
}
</style>
